// SPDX-License-Identifier: AGPL-3.0-or-later
import { useId, useState, type FormEvent } from "react";
import { monthlyPay, type PayFrequency, type Profile } from "../lib/categories.js";
import { useApp } from "../lib/app.js";
import { useI18n } from "../lib/i18n.js";
import { useStore } from "../lib/store.js";

const HOUSING = ["own", "rent", "family"] as const;
const VEHICLE = ["none", "owned", "financed", "leased"] as const;
const FREQUENCY: PayFrequency[] = ["weekly", "biweekly", "semimonthly", "monthly"];

/**
 * The setup survey. First run shows it full-page with a skip button; Settings
 * shows the same questions to change later. Every question is optional.
 */
export function HouseholdSetup({ onDone, firstRun }: { onDone?: () => void; firstRun: boolean }) {
  const { store, announce } = useApp();
  const { profile } = useStore(store);
  const { t, money } = useI18n();
  const id = useId();
  const [housing, setHousing] = useState<Profile["housing"]>(profile?.housing ?? null);
  const [vehicle, setVehicle] = useState<Profile["vehicle"]>(profile?.vehicle ?? null);
  const [amount, setAmount] = useState(profile?.pay ? (profile.pay.amount / 100).toFixed(2) : "");
  const [frequency, setFrequency] = useState<PayFrequency>(profile?.pay?.frequency ?? "biweekly");
  const [error, setError] = useState<string | null>(null);

  const cents = amount.trim() === "" ? null : Math.round(Number(amount.replace(/[$\s,]/g, "")) * 100);
  const validPay = cents !== null && Number.isFinite(cents) && cents > 0;

  const save = (e: FormEvent) => {
    e.preventDefault();
    if (cents !== null && !validPay) return setError(t("survey.payInvalid"));
    store.setProfile({ housing, vehicle, pay: validPay ? { frequency, amount: cents } : null });
    announce(t("survey.saved"));
    onDone?.();
  };

  const skip = () => {
    store.setProfile(profile ?? { housing: null, vehicle: null, pay: null });
    onDone?.();
  };

  return (
    <form onSubmit={save} noValidate>
      {firstRun && <p>{t("survey.intro")}</p>}
      <fieldset>
        <legend>{t("survey.housing")}</legend>
        <div className="choices">
          {HOUSING.map((h) => (
            <label key={h}>
              <input type="radio" name={`${id}-housing`} checked={housing === h} onChange={() => setHousing(h)} />
              {t(`survey.housing.${h}`)}
            </label>
          ))}
        </div>
      </fieldset>
      <fieldset>
        <legend>{t("survey.vehicle")}</legend>
        <div className="choices">
          {VEHICLE.map((v) => (
            <label key={v}>
              <input type="radio" name={`${id}-vehicle`} checked={vehicle === v} onChange={() => setVehicle(v)} />
              {t(`survey.vehicle.${v}`)}
            </label>
          ))}
        </div>
      </fieldset>
      <fieldset>
        <legend>{t("survey.pay")}</legend>
        <div className="toolbar" style={{ marginBottom: 6 }}>
          <div className="field" style={{ marginBottom: 0 }}>
            <label htmlFor={`${id}-amount`}>{t("survey.payAmount")}</label>
            <input
              id={`${id}-amount`}
              type="text"
              inputMode="decimal"
              value={amount}
              aria-invalid={error ? true : undefined}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label htmlFor={`${id}-frequency`}>{t("survey.payFrequency")}</label>
            <select id={`${id}-frequency`} value={frequency} onChange={(e) => setFrequency(e.target.value as PayFrequency)}>
              {FREQUENCY.map((f) => (
                <option key={f} value={f}>
                  {t(`survey.pay.${f}`)}
                </option>
              ))}
            </select>
          </div>
        </div>
        {validPay && (
          <p className="hint" aria-live="polite">
            {t("survey.payHint", { amount: money(monthlyPay({ frequency, amount: cents })) })}
          </p>
        )}
      </fieldset>
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <div className="row">
        <button type="submit" className="primary">
          {firstRun ? t("survey.save") : t("common.save")}
        </button>
        {firstRun && (
          <button type="button" onClick={skip}>
            {t("survey.skip")}
          </button>
        )}
      </div>
    </form>
  );
}
